import * as React from 'react'
import {ReactElement} from 'react'
import {NatApplicationRepo} from '../repository/NatApplicationRepo'
import {BlockSpacer} from '../../../../styledComponents/ResponsiveConvenience'
import {DisplayItemDividerForPageTop} from '../../../../styledComponents/DisplayItem'
import {StyledSecondaryButton} from '../../../../styledComponents/Button'
import {Box} from '@mui/material'
import {PageTitle} from '../common/resident/NatResidentApplicationsCommon'
import {
    createResidentWithErrorHandlingPage,
    ResidentWithErrorHandlingPagePropsSet,
} from '../../../../components/shared/hoc/createResidentWithErrorHandlingPage'
import {Nullable} from '../../../../app/TypeHelpers'
import {RESIDENT_APPLY_STATUS} from '../../../../CodeMaster'
import {NatResidentApplicationHistoryInfoComponent} from '../common/resident/NatResidentApplicationHistoryInfoComponent'
import styled from 'styled-components'
import {NatBicycleParkingUseApplyDetail} from '../repository/NatGetBicycleParkingUseApply'
import {FullWidthColumnFlexContainer} from '../../../../styledComponents/FlexComponents'
import {NatGetApplicationResultDetail} from '../repository/NatGetApplicationResult'
import {HKConstants} from '../../../../HKConstants'
import AuthorityChecker from '../../../../components/AuthorityChecker'
import NatResidentBicycleParkingUseApplyConfirm from './resident/NatResidentBicycleParkingUseApplyConfirm'
import {PlainBackLink} from '../../../../styledComponents/Links'
import {convertApplyStatusIntoApplyHistoryDivision} from '../common/helper/NatApplicationHelper'
import {Location} from 'react-router-dom'

interface Props {
    condominiumCd: string
    applyId: string
    applicationRepo: NatApplicationRepo
    location: Location
}

interface States {
    bicycleParkingUsesDetail: NatBicycleParkingUseApplyDetail
    applicationResultDetail: NatGetApplicationResultDetail
    isLoadCompleted: boolean
}

class NatResidentBicycleParkingUsesPage
    extends React.Component<Props & ResidentWithErrorHandlingPagePropsSet, States> {

    constructor(props: Props & ResidentWithErrorHandlingPagePropsSet) {
        super(props)

        this.state = {
            bicycleParkingUsesDetail: {
                applyId: '',
                applyStatus: '',
                condominiumName: '',
                ridgeName: '',
                roomNumber: '',
                applicantFamilyName: '',
                applicantFirstName: '',
                applyDate: '',
                useArea: '',
                useStartDate: '',
                userFamilyName: '',
                userFirstName: '',
                userAttribute: '',
                ownerFamilyName: '',
                ownerFirstName: '',
                bicycleRegistrationNumber: '',
                version: 0,
            },
            applicationResultDetail: {
                applyInfo: {
                    applyId: '',
                    applyCategory: '',
                    ridgeName: '',
                    roomNumber: '',
                    familyName: '',
                    firstName: '',
                    registerDatetime: '',
                },
                resultInfo: {
                    applyStatus: '',
                    roleId: '',
                    comment: '',
                    registerDatetime: '',
                },
            },
            isLoadCompleted: false,
        }

        this.onClickReApplyButton = this.onClickReApplyButton.bind(this)
    }

    async componentDidMount(): Promise<void> {
        await this.props.eventEmitter.showLoading()
        try {
            await this.getApplyInfo()
        } finally {
            this.props.eventEmitter.closeLoading()
            this.setState({
                    isLoadCompleted: true,
                },
            )
        }
    }

    async getApplyInfo(): Promise<void> {
        const [bicycleParkingUsesResponse, applicationStatusResponse] = await Promise.all([
            this.getBicycleParkingUses(),
            this.getApplicationResult(),
        ])

        if (bicycleParkingUsesResponse && applicationStatusResponse) {
            this.setState({
                bicycleParkingUsesDetail: bicycleParkingUsesResponse,
                applicationResultDetail: applicationStatusResponse,
            })
        }
    }

    async getBicycleParkingUses(): Promise<Nullable<NatBicycleParkingUseApplyDetail>> {
        const {condominiumCd, applyId, applicationRepo, eventEmitter} = this.props

        const response = await this.props.asyncCallFunction(
            applicationRepo.getBicycleParkingUseApplyForResident.bind(applicationRepo, condominiumCd, applyId),
            (httpErrorOrNetworkError) => {
                eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
            },
        )
        return response ? response.details : response
    }

    async getApplicationResult(): Promise<Nullable<NatGetApplicationResultDetail>> {
        const {condominiumCd, applyId, applicationRepo, eventEmitter} = this.props

        const response = await this.props.asyncCallFunction(
            applicationRepo.getApplyResult.bind(applicationRepo, condominiumCd, applyId),
            (httpErrorOrNetworkError) => {
                eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
            },
        )
        return response ? response.details : response
    }

    onClickReApplyButton(): void {
        this.props.navigate(
            `/resident/condominiums/${this.props.condominiumCd}/applications/bicycleParkingUses/${this.props.applyId}/copy`,
        )
    }

    render(): Nullable<ReactElement> {
        if (!this.state.isLoadCompleted) {
            return null
        }

        return (
            <>
                <PlainBackLink to={`/resident/condominiums/${this.props.condominiumCd}/applications/history`}>
                    戻る
                </PlainBackLink>
                <BlockSpacer height={'16px'}/>
                <DisplayItemDividerForPageTop/>
                <BlockSpacer height={'30px'}/>
                <PageTitle>
                    申請内容
                </PageTitle>
                <Box height={'20px'}/>
                <NatResidentApplicationHistoryInfoComponent
                    applyId={this.props.applyId}
                    applyHistoryDivision={convertApplyStatusIntoApplyHistoryDivision(
                        this.state.bicycleParkingUsesDetail.applyStatus)}
                    applyResultInfo={this.state.applicationResultDetail}
                />
                <NatResidentBicycleParkingUseApplyConfirm
                    {...this.state.bicycleParkingUsesDetail}
                    applyFlag={false}
                />
                <AuthorityChecker permittedAuthorities={HKConstants.CMN.AUTH.APPLY_BICYCLE_PARKING_USE_APPLICATION}>
                    {(this.state.bicycleParkingUsesDetail.applyStatus === RESIDENT_APPLY_STATUS.COMPLETE.value ||
                            this.state.bicycleParkingUsesDetail.applyStatus === RESIDENT_APPLY_STATUS.REJECTION.value) &&
                        <ReApplyButton
                            onClick={this.onClickReApplyButton}>
                            <FullWidthColumnFlexContainer>
                                <div>
                                    内容を編集して再申請する
                                </div>
                            </FullWidthColumnFlexContainer>
                        </ReApplyButton>
                    }
                </AuthorityChecker>
            </>
        )
    }
}

export default createResidentWithErrorHandlingPage(NatResidentBicycleParkingUsesPage)

export const ReApplyButton = styled(StyledSecondaryButton)`
  display: flex;
  align-items: center;
  margin-top: 30px;
  margin-bottom: 15px;
`
